import type {
  BookingDTO,
  MultiRideAccountDTO,
  ReductionCardAccountDTO,
  TravelPassAccountDTO,
} from '@/types/dto/agent';
import type {
  DecoratedTravelPassDetails,
  PartitionedTravelPassDetails,
  TravelAccountsMap,
} from '@/types/travelpass';
import { useMemo } from 'react';

export const useSeparateNonTripOffersBasedOnType = (
  nonTripOffers: BookingDTO['nonTripOffers'],
  travelAccountsMap: TravelAccountsMap | undefined
) =>
  useMemo(
    () =>
      partitionByAccountType(
        prepareNonTripOffers(nonTripOffers, travelAccountsMap)
      ),
    [nonTripOffers, travelAccountsMap]
  );

const prepareNonTripOffers = (
  nonTripOffers: BookingDTO['nonTripOffers'],
  travelAccountsMap?: TravelAccountsMap
): Array<DecoratedTravelPassDetails> => {
  const result: Array<DecoratedTravelPassDetails> = [];

  if (!travelAccountsMap) {
    return result;
  }

  for (const nonTripOffer of nonTripOffers || []) {
    for (const admission of nonTripOffer.admissions || []) {
      for (const fulfillment of admission.fulfillments || []) {
        const productDescriptiveText = admission.productDescriptiveTexts?.[0];

        result.push({
          id: admission.id,
          shortDescription: productDescriptiveText?.shortDescription,
          longDescription: productDescriptiveText?.description,
          exchangeable: admission.exchangeable,
          refundable: admission.refundable,
          controlNumber: fulfillment.controlNumber,
          issuer: fulfillment.issuer,
          price: admission.price,
          passengerIds: admission.passengerIds,
          validFrom: admission.validFrom,
          validUntil: admission.validUntil,
          status: admission.status,
          travelAccount:
            fulfillment.issuer && fulfillment.controlNumber
              ? travelAccountsMap[
                  `${fulfillment.issuer}-${fulfillment.controlNumber}`
                ]
              : undefined,
        });
      }
    }
  }

  return result;
};

const partitionByAccountType = (
  details: Array<DecoratedTravelPassDetails>
): PartitionedTravelPassDetails => {
  const multiRide = [];
  const travelPass = [];
  const reductionCard = [];

  for (const item of details) {
    switch (item.travelAccount?.objectType) {
      case 'MultiRideAccount':
        multiRide.push(
          item as unknown as DecoratedTravelPassDetails<MultiRideAccountDTO>
        );
        break;
      case 'TravelPassAccount':
        travelPass.push(
          item as unknown as DecoratedTravelPassDetails<TravelPassAccountDTO>
        );
        break;
      case 'ReductionCardAccount':
        reductionCard.push(
          item as unknown as DecoratedTravelPassDetails<ReductionCardAccountDTO>
        );
        break;
      default:
        break;
    }
  }

  return {
    multiRide,
    travelPass,
    reductionCard,
  };
};
